import type { ComponentPropsWithoutRef } from 'react';
import styles from './Input.module.scss';

// ============================================
// TYPES
// ============================================

export interface ToggleProps extends Omit<ComponentPropsWithoutRef<'button'>, 'onChange'> {
  /** Forwarded ref to the <button> element */
  ref?: React.Ref<HTMLButtonElement>;
  /** Whether the toggle is on */
  isChecked?: boolean;
  /** Partial state — track tinted, thumb stays left, sets aria-checked="mixed". Ignored when isChecked is true. */
  isMixed?: boolean;
  /** Callback when toggle state changes */
  onChange?: (checked: boolean) => void;
  /** Layout variant. `inline` (default) places the toggle beside `label`. `card` renders a full-width row: heading + description on the left, toggle on the right edge, with a green-tint background when checked. */
  variant?: 'inline' | 'card';
  /** Label / heading text. In `inline` variant, sits beside the toggle. In `card` variant, renders as the row heading. */
  label?: string;
  /** Position of the label relative to the toggle. Ignored in `card` variant (label is always on the left). */
  labelPosition?: 'left' | 'right';
  /** Body / supporting copy. Only used in `card` variant — renders below the heading in a subdued style. */
  description?: string;
}

// ============================================
// COMPONENT
// ============================================

function Toggle({
  ref,
  isChecked = false,
  isMixed = false,
  onChange,
  variant = 'inline',
  label,
  labelPosition = 'right',
  description,
  disabled = false,
  className,
  ...rest
}: ToggleProps) {
  const handleClick = () => {
    if (!disabled) {
      onChange?.(!isChecked);
    }
  };

  const showMixed = isMixed && !isChecked;

  const toggleClasses = [
    styles.toggle,
    isChecked && styles.toggleChecked,
    showMixed && styles.toggleMixed,
  ]
    .filter(Boolean)
    .join(' ');

  const toggleButton = (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={showMixed ? 'mixed' : isChecked}
      disabled={disabled}
      className={toggleClasses}
      onClick={handleClick}
      {...rest}
    />
  );

  if (variant === 'card') {
    const cardClasses = [
      styles.toggleCard,
      isChecked && styles.toggleCardChecked,
      disabled && styles.toggleCardDisabled,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return;
      // Click originating inside the toggle button is handled by its own click handler.
      if ((e.target as HTMLElement).closest('button[role="switch"]')) return;
      onChange?.(!isChecked);
    };

    return (
      <div className={cardClasses} onClick={handleCardClick}>
        <div className={styles.toggleCardBody}>
          {label && <span className={styles.toggleCardHeader}>{label}</span>}
          {description && (
            <span className={styles.toggleCardDescription}>{description}</span>
          )}
        </div>
        {toggleButton}
      </div>
    );
  }

  if (!label) return toggleButton;

  return (
    <div className={[styles.toggleWrapper, className].filter(Boolean).join(' ')}>
      {labelPosition === 'left' && (
        <span className={styles.toggleLabel}>{label}</span>
      )}
      {toggleButton}
      {labelPosition === 'right' && (
        <span className={styles.toggleLabel}>{label}</span>
      )}
    </div>
  );
}

export default Toggle;
